import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';
import sha from 'sha.js';
import { useLocation } from 'react-router-dom';
import { Button, Clickable, Input, Stack, Text } from '@moda/om';
import ExitIcon from '@moda/icons/exit-16';
import { Link } from '../Link';
import { findRouteFor } from '../../routers';
import { ModalOverlay } from '../Modal';
import { useEmailSubscribe } from '../../hooks/useEmailSubscribe';
import { hrefFor } from '../../routers';
import { usePreferences } from '../../hooks/usePreferences';
import { useUser } from '../../hooks/useUser';
import { tc } from '../../lib/trackingContext';

import './EmailSignup.scss';

const PAGES_TO_VIEW_BEFORE_MODAL_SHOWS = 4;

export const EmailSignup: React.FC = () => {
  const [email, setEmail] = useState<string>('');
  const { showEmailSignup, setPreference } = usePreferences();
  const history = useHistory();
  const { user } = useUser();

  const {
    subscribe,
    isSubscribed,
    isExistingSubscription,
    error: mutationError,
    mailValidationError
  } = useEmailSubscribe();

  const error = mutationError || mailValidationError;
  const { pathname } = useLocation();
  const route = findRouteFor(pathname);
  const pagetype = route?.pagetype ?? null;

  const handleSubmit = useCallback(
    event => {
      event.preventDefault();
      subscribe(email, 'welcomePopup');
      tc.track('Welcome Email Modal Sign Up Finished', {
        addToTrackingEvent: {
          pagetype
        }
      });
    },
    [email, subscribe, pagetype]
  );

  const trackModalDismissed = useCallback(() => {
    tc.track('Welcome Email Modal Dismissed', {
      addToTrackingEvent: {
        pagetype
      }
    });
  }, [pagetype]);

  useEffect(() => {
    if (user.isLoggedIn && showEmailSignup) setPreference('showEmailSignup', false);
  }, [user.isLoggedIn, showEmailSignup, setPreference]);

  const showModal =
    history.length >= PAGES_TO_VIEW_BEFORE_MODAL_SHOWS &&
    showEmailSignup &&
    !user.isLoggedIn &&
    pathname !== hrefFor.CheckoutPage() &&
    pathname !== hrefFor.ShoppingBagPage();

  useEffect(() => {
    if (showModal) {
      tc.track('Welcome Email Modal Viewed', {
        addToTrackingEvent: {
          pagetype
        }
      });
    }
  }, [pagetype, showModal]);

  return (
    <ModalOverlay
      onClose={() => {
        setPreference('showEmailSignup', false);
        trackModalDismissed();
      }}
      show={showModal}
    >
      <div className="EmailSignup">
        <Clickable
          className="EmailSignup__close"
          onClick={() => {
            setPreference('showEmailSignup', false);
            trackModalDismissed();
          }}
        >
          <ExitIcon />
        </Clickable>
        <Stack className="EmailSignup__content" alignItems="center" space={4}>
          {!isSubscribed && (
            <Text className="EmailSignup__title" family="serif" treatment="h3">
              Get On The List!
            </Text>
          )}
          {isSubscribed && (
            <Text className="EmailSignup__title" family="serif" treatment="h3">
              You're on the list.
            </Text>
          )}

          {!isSubscribed && (
            <Stack alignItems="center" space={2}>
              <Text treatment="h6">Love what you're seeing?</Text>
              <Text treatment="h6">
                Enjoy <span className="EmailSignup__discount">15% off*</span> your first purchase
                and be the first to receive updates about what’s new and next in fashion, sent
                straight to your inbox.
              </Text>
            </Stack>
          )}
          {isSubscribed && (
            <>
              {isExistingSubscription && (
                <div className="EmailSignup__status">
                  Looks like you're already on our newsletter list,{' '}
                  <Link
                    className="EmailSignup__manage-subscriptions-link"
                    target="_blank"
                    rel="noopener noreferrer"
                    to={`${hrefFor.UnsubscribePage()}?email_hash=${sha('sha256')
                      .update(email)
                      .digest('hex')}`}
                  >
                    click here
                  </Link>{' '}
                  to manage your subscriptions.
                </div>
              )}
              {!isExistingSubscription && (
                <Text treatment="h6">
                  Now check your email—your <span className="EmailSignup__discount">15% off</span>{' '}
                  is waiting for you.
                </Text>
              )}
            </>
          )}

          {!isSubscribed && (
            <form className="EmailSignup__form" onSubmit={handleSubmit}>
              <Input
                className="EmailSignup__input"
                name="email"
                placeholder="E-mail Address"
                type="email"
                error={!!error}
                required
                value={email}
                onChange={({ currentTarget: { value } }) => setEmail(value)}
              />
              {error && (
                <div
                  className={classNames('EmailSignup__status', {
                    'EmailSignup__status--error': error
                  })}
                >
                  {mailValidationError || 'Unable to subscribe you at this time.'}
                </div>
              )}
              <Button type="submit" role="button">
                SIGN UP
              </Button>
            </form>
          )}

          {isSubscribed && (
            <Button
              className="EmailSignup__action"
              onClick={() => setPreference('showEmailSignup', false)}
              role="button"
            >
              CONTINUE SHOPPING
            </Button>
          )}

          <Stack space={2} className="EmailSignup__footer">
            <Text treatment="body2" color="cement">
              To opt-out of email sharing or to view our Privacy Policy, click{' '}
              <Link
                className="EmailSignup__link"
                to="/privacy"
                rel="noopener noreferrer"
                target="_blank"
              >
                here
              </Link>
            </Text>
            {!isSubscribed && (
              <Text treatment="body2" color="cement">
                *Terms and Conditions Apply
              </Text>
            )}
          </Stack>
        </Stack>
      </div>
    </ModalOverlay>
  );
};
